/**
 * Embed Code Generator Component
 * Generates embed code for chat widgets (flipbook or webpage chat)
 */

import { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { updateWebpageChatDomains } from "../../services/content.service";
import { useToast } from "../ui/Toast";
import Button from "../common/Button";
import type { UUID } from "../../types/common.types";
import {
  ClipboardDocumentIcon,
  CheckIcon,
  CodeBracketIcon,
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";

interface EmbedCodeGeneratorProps {
  contentId: UUID;
  type: "flipbook" | "webpage";
  allowedDomains?: string[];
  onDomainsChange?: (domains: string[]) => void;
}

interface WidgetConfig {
  position: "bottom-right" | "bottom-left";
  primaryColor: string;
  greeting: string;
  buttonSize: "small" | "medium" | "large";
}

const DEFAULT_CONFIG: WidgetConfig = {
  position: "bottom-right",
  primaryColor: "#1e40af",
  greeting: "Hi! Ask me anything about this page.",
  buttonSize: "medium",
};

const POSITION_OPTIONS = [
  { value: "bottom-right", label: "Bottom Right" },
  { value: "bottom-left", label: "Bottom Left" },
];

const SIZE_OPTIONS = [
  { value: "small", label: "Small (48px)" },
  { value: "medium", label: "Medium (56px)" },
  { value: "large", label: "Large (64px)" },
];

export default function EmbedCodeGenerator({
  contentId,
  type,
  allowedDomains = [],
  onDomainsChange,
}: EmbedCodeGeneratorProps) {
  const { showToast } = useToast();

  // Configuration state
  const [config, setConfig] = useState<WidgetConfig>(DEFAULT_CONFIG);
  const [showConfig, setShowConfig] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Domain management state
  const [domains, setDomains] = useState<string[]>(allowedDomains);
  const [domainInput, setDomainInput] = useState("");

  // Copy state
  const [copied, setCopied] = useState(false);

  // Update domains mutation
  const updateDomainsMutation = useMutation({
    mutationFn: async (newDomains: string[]) => {
      await updateWebpageChatDomains(contentId, newDomains);
    },
    onSuccess: () => {
      showToast("success", "Allowed domains updated");
      onDomainsChange?.(domains);
    },
    onError: (error: Error) => {
      showToast("error", `Failed to update domains: ${error.message}`);
    },
  });

  // Generate the embed code
  const embedCode = useMemo(() => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "[SUPABASE_URL]";
    const configJson = JSON.stringify(
      {
        contentId,
        position: config.position,
        primaryColor: config.primaryColor,
        greeting: config.greeting,
        buttonSize: config.buttonSize,
      },
      null,
      2,
    );

    return `<!-- USLS Chat Widget -->
<script src="${supabaseUrl}/functions/v1/chat-widget?contentId=${contentId}"></script>
<script>
  window.USLSChatConfig = ${configJson};
</script>`;
  }, [contentId, config]);

  // Copy embed code to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      showToast("success", "Embed code copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast("error", "Failed to copy to clipboard");
    }
  };

  // Add domain
  const handleAddDomain = () => {
    const trimmedDomain = domainInput.trim().toLowerCase();
    if (trimmedDomain && !domains.includes(trimmedDomain)) {
      // Basic domain validation
      if (
        /^(\*\.)?[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)+$/.test(
          trimmedDomain,
        )
      ) {
        const newDomains = [...domains, trimmedDomain];
        setDomains(newDomains);
        setDomainInput("");
        if (type === "webpage") {
          updateDomainsMutation.mutate(newDomains);
        }
      } else {
        showToast(
          "error",
          "Invalid domain format. Use format: example.com or *.example.com",
        );
      }
    }
  };

  // Remove domain
  const handleRemoveDomain = (domainToRemove: string) => {
    const newDomains = domains.filter((d) => d !== domainToRemove);
    setDomains(newDomains);
    if (type === "webpage") {
      updateDomainsMutation.mutate(newDomains);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CodeBracketIcon className="h-5 w-5 text-gray-500" />
          <h3 className="text-sm font-medium text-gray-900">
            Embed Chat Widget
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title="Preview"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setShowConfig(!showConfig)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title="Configure"
          >
            <Cog6ToothIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <h4 className="text-sm font-medium text-gray-700">
            Widget Configuration
          </h4>

          {/* Position */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Position
            </label>
            <select
              value={config.position}
              onChange={(e) =>
                setConfig({
                  ...config,
                  position: e.target.value as WidgetConfig["position"],
                })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {POSITION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Button Size */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Button Size
            </label>
            <select
              value={config.buttonSize}
              onChange={(e) =>
                setConfig({
                  ...config,
                  buttonSize: e.target.value as WidgetConfig["buttonSize"],
                })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {SIZE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Primary Color */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Primary Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={config.primaryColor}
                onChange={(e) =>
                  setConfig({ ...config, primaryColor: e.target.value })
                }
                className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={config.primaryColor}
                onChange={(e) =>
                  setConfig({ ...config, primaryColor: e.target.value })
                }
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
              />
            </div>
          </div>

          {/* Greeting */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Greeting Message
            </label>
            <input
              type="text"
              value={config.greeting}
              onChange={(e) =>
                setConfig({ ...config, greeting: e.target.value })
              }
              placeholder="Hi! How can I help you?"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Reset to defaults */}
          <button
            type="button"
            onClick={() => setConfig(DEFAULT_CONFIG)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Reset to defaults
          </button>
        </div>
      )}

      {/* Preview */}
      {showPreview && (
        <div className="bg-gray-100 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Preview</h4>
          <div
            className="relative bg-white rounded-lg shadow-lg overflow-hidden"
            style={{ height: "200px" }}
          >
            {/* Mock webpage content */}
            <div className="p-4 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>

            {/* Mock chat button */}
            <div
              className={`absolute ${config.position === "bottom-right" ? "right-4" : "left-4"} bottom-4`}
            >
              <div
                className={`rounded-full shadow-lg flex items-center justify-center text-white ${
                  config.buttonSize === "small"
                    ? "w-12 h-12"
                    : config.buttonSize === "medium"
                      ? "w-14 h-14"
                      : "w-16 h-16"
                }`}
                style={{ backgroundColor: config.primaryColor }}
              >
                <svg
                  className={
                    config.buttonSize === "small"
                      ? "w-6 h-6"
                      : config.buttonSize === "medium"
                        ? "w-7 h-7"
                        : "w-8 h-8"
                  }
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Allowed Domains (for webpage chat) */}
      {type === "webpage" && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">
            Allowed Domains
          </label>
          <p className="text-xs text-gray-500 mb-2">
            The widget will only load on these domains. Use *.domain.com for
            subdomains.
          </p>
          <div className="flex flex-wrap gap-2 mb-2">
            {domains.map((domain) => (
              <span
                key={domain}
                className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full"
              >
                {domain}
                <button
                  type="button"
                  onClick={() => handleRemoveDomain(domain)}
                  className="text-green-600 hover:text-red-500"
                  disabled={updateDomainsMutation.isPending}
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
            {domains.length === 0 && (
              <span className="text-xs text-gray-400 italic">
                No domains configured - widget will work on any domain
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddDomain();
                }
              }}
              placeholder="example.com"
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={updateDomainsMutation.isPending}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleAddDomain}
              disabled={updateDomainsMutation.isPending}
            >
              <PlusIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Embed Code */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">
          Embed Code
        </label>
        <div className="relative">
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto font-mono">
            {embedCode}
          </pre>
          <button
            type="button"
            onClick={handleCopy}
            className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            title="Copy to clipboard"
          >
            {copied ? (
              <CheckIcon className="h-4 w-4 text-green-400" />
            ) : (
              <ClipboardDocumentIcon className="h-4 w-4" />
            )}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Add this code to your website's HTML, just before the closing{" "}
          <code className="bg-gray-100 px-1 rounded">&lt;/body&gt;</code> tag.
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <h4 className="text-sm font-medium text-blue-800 mb-2">
          Integration Instructions
        </h4>
        <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
          <li>Copy the embed code above</li>
          <li>
            Paste it into your website's HTML before the{" "}
            <code className="bg-blue-100 px-1 rounded">&lt;/body&gt;</code> tag
          </li>
          <li>The chat widget will appear as a floating button</li>
          <li>Visitors can click it to ask questions about the content</li>
        </ol>
      </div>
    </div>
  );
}
